import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {Fragment} from 'react';

import styles from './Roadmap.module.css';
import {ROADMAP_STATUS_COLORS} from './roadmapStatusColors';
import type {DashboardProgress, DashboardProgressBucket} from './types';

const formatPercent = (value: number) => `${Math.round(value * 100)}%`;

const formatShare = (count: number, total: number) =>
  total === 0 ? '0%' : formatPercent(count / total);

export const DASHBOARD_PROGRESS_BUCKET_LABELS: Record<DashboardProgressBucket, string> = {
  total: 'All',
  completed: 'Done',
  onTrack: 'In Progress',
  atRiskDelayed: 'At Risk/Delayed/Blocked/On Hold',
  notStarted: 'Backlog',
  cancelled: 'Cancelled',
};

const DASHBOARD_PROGRESS_BUCKETS: DashboardProgressBucket[] = [
  'total',
  'completed',
  'onTrack',
  'atRiskDelayed',
  'notStarted',
  'cancelled',
];

const DashboardProgressLabel = ({label}: {label: string}) => {
  const segments = label.split('/');

  return (
    <Text size="small" className={styles.dashboardProgressLabel}>
      {segments.length === 1
        ? label
        : segments.map((segment, index) => {
            const hasNextSegment = index < segments.length - 1;
            return (
              <Fragment key={`${segment}-${index}`}>
                <span className={styles.dashboardProgressLabelSegment}>
                  {segment}
                  {hasNextSegment ? '/' : null}
                </span>
                {hasNextSegment ? <wbr /> : null}
              </Fragment>
            );
          })}
    </Text>
  );
};

const DashboardProgressCard = ({
  label,
  value,
  percent,
  bucket,
  isSelected,
  onClick,
}: {
  label: string;
  value: number;
  percent: string;
  bucket: DashboardProgressBucket;
  isSelected?: boolean;
  onClick?: () => void;
}) => {
  const content = (
    <>
      <DashboardProgressLabel label={label} />
      <div className={styles.dashboardProgressValue} style={{color: ROADMAP_STATUS_COLORS[bucket]}}>
        {value} <span className={styles.dashboardProgressPercent}>({percent})</span>
      </div>
    </>
  );

  if (!onClick) {
    return <div className={styles.dashboardProgressCard}>{content}</div>;
  }

  return (
    <button
      type="button"
      className={styles.dashboardProgressCardButton}
      data-progress-bucket={bucket}
      aria-pressed={isSelected}
      onClick={onClick}
    >
      {content}
    </button>
  );
};

interface DashboardProgressStripProps {
  dashboardProgress: DashboardProgress;
  onDashboardBucketClick: (bucket: DashboardProgressBucket) => void;
  selectedBucket?: DashboardProgressBucket | null;
}

export const DashboardProgressStrip = ({
  dashboardProgress,
  onDashboardBucketClick,
  selectedBucket,
}: DashboardProgressStripProps) => (
  <div
    className={styles.dashboardProgressStrip}
    role="group"
    aria-label="Roadmap initiative status summary"
  >
    {DASHBOARD_PROGRESS_BUCKETS.map((bucket) => {
      const value = dashboardProgress[bucket];
      return (
        <DashboardProgressCard
          key={bucket}
          label={DASHBOARD_PROGRESS_BUCKET_LABELS[bucket]}
          value={value}
          percent={bucket === 'total' ? '100%' : formatShare(value, dashboardProgress.total)}
          bucket={bucket}
          isSelected={selectedBucket === undefined ? undefined : selectedBucket === bucket}
          onClick={() => onDashboardBucketClick(bucket)}
        />
      );
    })}
  </div>
);

export const DashboardProgressStripSkeleton = () => (
  <div
    className={styles.dashboardProgressStrip}
    data-testid="dashboard-progress-strip-loading"
    aria-hidden="true"
  >
    {DASHBOARD_PROGRESS_BUCKETS.map((bucket) => (
      <div key={bucket} className={styles.dashboardProgressCard}>
        <Skeleton.Text width={72} />
        <Skeleton.Text width={48} />
      </div>
    ))}
  </div>
);
